import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarRange, Filter, X } from 'lucide-react';
import type { GranularidadeDeRelatorio } from '@central/contracts';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { cn } from '@/lib/cn';
import { chavesDeApoio, obterOpcoesDeFormulario } from '@/features/tickets/lookups.api';
import type { FiltroDeRelatorio, TipoDeChamado } from '@/features/relatorios/relatorios.api';
import { ultimosDias } from '@/features/relatorios/periodo';

/**
 * O recorte, compartilhado pelas cinco abas.
 *
 * Mora num contexto pendurado na casca, e não em cada tela, porque trocar de
 * aba não pode zerar o filtro: quem está olhando "agosto, equipe Infra" no
 * volume quer ver o mesmo agosto e a mesma Infra na aderência de ANS. Estado
 * por tela obrigaria a refazer a escolha cinco vezes — e a quinta seria
 * diferente das outras quatro sem ninguém notar.
 */

export interface EstadoDoFiltro {
  /** `AAAA-MM-DD`, do jeito que o `<input type="date">` guarda. */
  de: string;
  ate: string;
  granularidade: GranularidadeDeRelatorio | 'auto';
  organizationId: string;
  teamId: string;
  tipos: TipoDeChamado[];
}

interface Contexto {
  estado: EstadoDoFiltro;
  definir: Dispatch<SetStateAction<EstadoDoFiltro>>;
  /** O mesmo recorte no formato que a API recebe. */
  consulta: FiltroDeRelatorio;
}

const ContextoDoFiltro = createContext<Contexto | null>(null);

export function filtroInicial(agora = new Date()): EstadoDoFiltro {
  return {
    // "Até hoje" quer dizer hoje: a API resolve a data final como o começo do
    // dia local seguinte, então o dia corrente entra inteiro. Apontar para
    // amanhã, como se fazia aqui, agora pediria um dia que ainda não existe.
    ...ultimosDias(30, agora),
    granularidade: 'auto',
    organizationId: '',
    teamId: '',
    tipos: [],
  };
}

export function ProvedorDeFiltro({ children }: { children: ReactNode }) {
  const [estado, definir] = useState<EstadoDoFiltro>(() => filtroInicial());

  const valor = useMemo<Contexto>(
    () => ({
      estado,
      definir,
      consulta: {
        de: estado.de,
        ate: estado.ate,
        ...(estado.granularidade === 'auto' ? {} : { granularidade: estado.granularidade }),
        ...(estado.organizationId ? { organizationId: estado.organizationId } : {}),
        ...(estado.teamId ? { teamId: estado.teamId } : {}),
        ...(estado.tipos.length ? { tipo: estado.tipos } : {}),
      },
    }),
    [estado],
  );

  return <ContextoDoFiltro.Provider value={valor}>{children}</ContextoDoFiltro.Provider>;
}

export function useFiltro(): Contexto {
  const contexto = useContext(ContextoDoFiltro);
  if (!contexto) throw new Error('useFiltro precisa estar dentro de <ProvedorDeFiltro>.');
  return contexto;
}

// ── a barra ────────────────────────────────────────────────────────────────

const ATALHOS: Array<[rotulo: string, dias: number]> = [
  ['7 dias', 7],
  ['30 dias', 30],
  ['90 dias', 90],
  ['12 meses', 365],
];

const TIPOS: Array<[TipoDeChamado, string]> = [
  ['INCIDENT', 'Incidente'],
  ['REQUEST', 'Requisição'],
  ['PROBLEM', 'Problema'],
  ['CHANGE', 'Mudança'],
  ['TASK', 'Tarefa'],
];

const GRANULARIDADES: Array<[EstadoDoFiltro['granularidade'], string]> = [
  ['auto', 'Automático'],
  ['dia', 'Por dia'],
  ['semana', 'Por semana'],
  ['mes', 'Por mês'],
];

const CAMPO =
  'h-8 rounded-md border border-border bg-background px-2 text-sm ' +
  'focus-visible:shadow-glow focus-visible:outline-none';

export function BarraDeFiltro() {
  const { estado, definir } = useFiltro();

  // As mesmas opções que o formulário de chamado usa. Uma rota nova só para o
  // relatório daria dois vocabulários para as mesmas equipes e organizações.
  const opcoes = useQuery({
    queryKey: chavesDeApoio.formulario,
    queryFn: obterOpcoesDeFormulario,
    staleTime: 5 * 60_000,
  });

  const aplicarAtalho = (dias: number) =>
    definir((atual) => ({ ...atual, ...ultimosDias(dias) }));

  const alternarTipo = (tipo: TipoDeChamado) =>
    definir((atual) => ({
      ...atual,
      tipos: atual.tipos.includes(tipo)
        ? atual.tipos.filter((t) => t !== tipo)
        : [...atual.tipos, tipo],
    }));

  const temFiltro =
    estado.organizationId !== '' || estado.teamId !== '' || estado.tipos.length > 0;

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarRange className="h-4 w-4 text-muted-foreground" aria-hidden />
          <label className="text-sm text-muted-foreground" htmlFor="relatorio-de">
            De
          </label>
          <input
            id="relatorio-de"
            type="date"
            value={estado.de}
            onChange={(evento) => definir((atual) => ({ ...atual, de: evento.target.value }))}
            className={CAMPO}
          />
          <label className="text-sm text-muted-foreground" htmlFor="relatorio-ate">
            até
          </label>
          <input
            id="relatorio-ate"
            type="date"
            value={estado.ate}
            onChange={(evento) => definir((atual) => ({ ...atual, ate: evento.target.value }))}
            className={CAMPO}
          />
          <span
            className="text-xs text-muted-foreground"
            title="A data final é resolvida no fuso do calendário padrão do tenant."
          >
            (os dois dias entram inteiros)
          </span>

          <div className="ml-auto flex flex-wrap items-center gap-1">
            {ATALHOS.map(([rotulo, dias]) => (
              <button
                key={rotulo}
                type="button"
                onClick={() => aplicarAtalho(dias)}
                className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:shadow-glow focus-visible:outline-none"
              >
                {rotulo}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" aria-hidden />

          <select
            aria-label="Organização"
            value={estado.organizationId}
            onChange={(evento) =>
              definir((atual) => ({ ...atual, organizationId: evento.target.value }))
            }
            className={CAMPO}
          >
            <option value="">Todas as organizações</option>
            {(opcoes.data?.organizations ?? []).map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Equipe"
            value={estado.teamId}
            onChange={(evento) => definir((atual) => ({ ...atual, teamId: evento.target.value }))}
            className={CAMPO}
          >
            <option value="">Todas as equipes</option>
            {(opcoes.data?.teams ?? []).map((time) => (
              <option key={time.id} value={time.id}>
                {time.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Agrupamento da série"
            value={estado.granularidade}
            onChange={(evento) =>
              definir((atual) => ({
                ...atual,
                granularidade: evento.target.value as EstadoDoFiltro['granularidade'],
              }))
            }
            className={CAMPO}
          >
            {GRANULARIDADES.map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </select>

          <div className="flex flex-wrap items-center gap-1">
            {TIPOS.map(([tipo, rotulo]) => {
              const ativo = estado.tipos.includes(tipo);
              return (
                <button
                  key={tipo}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => alternarTipo(tipo)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:shadow-glow focus-visible:outline-none',
                    ativo
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {rotulo}
                </button>
              );
            })}
          </div>

          {temFiltro ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                definir((atual) => ({ ...atual, organizationId: '', teamId: '', tipos: [] }))
              }
              icon={<X className="h-3.5 w-3.5" aria-hidden />}
            >
              Limpar
            </Button>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
